import { isSuccess } from '@devexperts/remote-data-ts'
import { QuoteReservation } from '@/models/quote.ts'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice.ts'
import { cartSlice } from '@/store/cartSlice'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice.ts'
import { licenseSlice } from '@/store/licenseSlice/index.tsx'
import { getReservationSpotId } from '@/utils/getReservationSpotId.ts'
import { SummaryProductSelected } from '@/utils/recap.ts'
import {
  getGroupLabel,
  getItemGroups,
} from '@/utils/timeSchedule/groupItems.ts'
import { adjustEndTime, isFullDay } from '@/utils/timeSchedules.ts'
import { SpotType } from '../models/cart.ts'
import { servicesSlice } from '../store/servicesSlice'
import { getVisibleServices } from '../utils/getVisibleServices.ts'
import { isSpotWithSeats } from '../utils/isWithVisibleSeats.ts'

import { useAppSelector } from './store.ts'

export function useCartServicesMapper(
  reservation: QuoteReservation,
  locale: string
) {
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const servicesData = useAppSelector(servicesSlice.selectors.data)
  const selectedBookingType = useAppSelector(
    cartSlice.selectors.selectedBookingType
  )

  const includedServices = servicesData?.includedServices ?? []
  const extraServices = servicesData?.extraServices ?? []
  const ticketServices = servicesData?.ticketServices ?? []
  const featuredServices = useAppSelector(
    servicesSlice.selectors.featuredServices
  )
  const license = useAppSelector(licenseSlice.selectors.license)

  const visibleServices = getVisibleServices({
    selectedElementHasVisibleSeats: isSpotWithSeats(
      reservation.spotType as SpotType
    ),
    services: [...includedServices, ...extraServices, ...ticketServices],
  })

  const serviceIds = [
    ...(selectedBookingType === BookingAvailability.TICKETS
      ? [
          ticketServices,
          isSuccess(featuredServices) ? featuredServices.value : [],
        ].flat()
      : visibleServices),
  ].map((s) => s.serviceId)

  return Object.keys(reservation.boughtServices).reduce<
    SummaryProductSelected[]
  >((acc, key) => {
    const serviceId = Number(key)

    if (!serviceIds.includes(serviceId)) {
      return acc
    }

    const boughtServices = reservation.boughtServices[serviceId]
    if (!boughtServices) {
      return acc
    }

    const groupItems = getItemGroups(
      [reservation],
      { spotName: reservation.spotName, spotType: reservation.spotType },
      serviceId
    )

    const services = groupItems.reduce<SummaryProductSelected[]>(
      (acc, group) => {
        const t = boughtServices[`${group.startTime} - ${group.endTime}`]
        if (!t) {
          return acc
        }

        function getName() {
          const suffix = t.timeSchedule.name ? ` · ${t.timeSchedule.name}` : ''
          const baseName = t.serviceName + suffix

          if (isFullDay(group)) {
            return baseName
          }

          return `${baseName} · ${group.startTime}-${adjustEndTime(group.endTime)}`
        }

        const subLabel = getGroupLabel({
          dayDifference,
          group,
          license,
          locale,
        })

        acc.push({
          id: serviceId,
          isExtra: true,
          isGift: false,
          name: getName(),
          quantity: group.quantity,
          spotId: getReservationSpotId(reservation),
          ...(subLabel !== null ? { subLabel } : {}),
        })

        return acc
      },
      []
    )

    return acc.concat(...services)
  }, [])
}

export function useCartSelectedServices(
  reservations: QuoteReservation[],
  locale: string
) {
  return reservations.flatMap((reservation) =>
    useCartServicesMapper(reservation, locale)
  )
}
